<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, type PropType, reactive, ref, watch } from 'vue'
  import {
    type CustomerCase as Case,
    type CustomerEntity,
    type CustomerEntityWithRelationship as EntityWithRelationship,
    EntityTypeEnum,
    RelationshipTypeEnum
  } from '@/types/generated'
  import Entity from '@/components/app/cases/entities/Entity.vue'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { Building2, Landmark, Minus, MoreVertical, PlusIcon, Trash2, User } from 'lucide-vue-next'
  import { useRoute } from 'vue-router'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import { yup } from '@/lib/validation.ts'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'

  const { t } = useTranslation({
    en: {
      parties: 'Parties',
      addNewParty: 'Add new party',
      selectParty: 'Select a party from the list',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      selectAllEntities: 'Select all entities',
      deselectAllEntities: 'Deselect all entities',
      ...Object.fromEntries(Object.entries(entitiesEN).map(([key, value]) => [key, value])),
      unnamed: 'Unnamed',
      notEnabledInExport: 'Not enabled in this export',
      delete_party: 'Delete party',
      confirm_delete: 'Confirm deletion',
      confirm_delete_party_message:
        'Are you sure you want to delete this party? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete',
      helpEntities: 'Learn more about Entities',
      selected: 'Selected'
    },
    de: {
      parties: 'Beteiligte',
      addNewParty: 'Beteiligten hinzufügen',
      selectParty: 'Wählen Sie einen Beteiligten aus der Liste aus',
      selectAll: 'Alle auswählen',
      deselectAll: 'Alle abwählen',
      selectAllEntities: 'Alle Beteiligte auswählen',
      deselectAllEntities: 'Alle Beteiligte abwählen',
      ...Object.fromEntries(Object.entries(entitiesDE).map(([key, value]) => [key, value])),
      unnamed: 'Unbenannt',
      notEnabledInExport: 'In diesem Export nicht verfügbar',
      delete_party: 'Beteiligten löschen',
      confirm_delete: 'Löschen bestätigen',
      selected: 'Ausgewählt',
      confirm_delete_party_message:
        'Sind Sie sicher, dass Sie diesen Beteiligten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      helpEntities: 'Mehr über Beteiligte erfahren'
    }
  })

  const emit = defineEmits(['update:selected-entities-relationships', 'onTotalNumberOfErrors'])

  // Clean function to format entity names based on entity type
  const formatEntityName = (entity: CustomerEntity | null | undefined): string => {
    if (!entity) return t('unnamed')

    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON: {
        const naturalPersonName =
          `${entity.title || ''} ${entity.first_name || ''} ${entity.last_name || ''}`.trim()
        return naturalPersonName || t('unnamed')
      }

      case EntityTypeEnum.LEGAL_PERSON:
        return entity.company_name || t('unnamed')

      case EntityTypeEnum.INSTITUTION:
        return entity.institution_name || t('unnamed')

      default:
        return t('unnamed')
    }
  }

  const props = defineProps({
    unifiedExportMode: {
      type: Boolean,
      required: false,
      default: false
    },
    insuranceInfoEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    opponentEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    miscEntitiesEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    customExportValidation: {
      type: Object as PropType<Record<string, yup.AnySchema> | null>,
      required: false,
      default: () => {}
    }
  })

  const caseStore = useCaseStore()
  const route = useRoute()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const clientEntityRelationship = computed<string | null>(() => {
    const clientEntity = caseRecord.value?.data.entities.find((entity) =>
      entity.relationships.some(
        (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    )
    return clientEntity?.uuid
  })
  const selectedEntityRelationship = ref<EntityWithRelationship | null>(null)

  // Variables for delete confirmation modal
  const showDeleteConfirmation = ref(false)
  const entityToDelete = ref<EntityWithRelationship | null>(null)

  // Popover state management
  const openPopoverId = ref<string | null>(null)

  // Helper functions to check entity types
  const isClientEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  }

  const isOpponentEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  }

  const isPriorityEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity) return false
    return isClientEntity(entity) || isOpponentEntity(entity)
  }

  const isEntityEnabled = (entityWithRelationship: EntityWithRelationship) => {
    if (!entityWithRelationship) {
      return false
    }
    // if the entity is a client, then it is it enabled and selectable
    if (
      entityWithRelationship.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    ) {
      return true
    }
    // if the entity is an opponent, then the entity is dependent on
    // props.opponentEnabled
    if (
      entityWithRelationship.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    ) {
      return props.opponentEnabled
    }
    // any other kind of (misc) entity is dependent on props.miscEntitiesEnabled
    return props.miscEntitiesEnabled
  }

  // Sort entities to ensure client is first, opponent is second, then all others
  const sortedEntities = computed(() => {
    if (!caseRecord.value?.data.entities) return []

    return [...caseRecord.value.data.entities].sort((a, b) => {
      const aIsClient = isClientEntity(a)
      const bIsClient = isClientEntity(b)
      const aIsOpponent = isOpponentEntity(a)
      const bIsOpponent = isOpponentEntity(b)

      if (aIsClient && !bIsClient) return -1
      if (!aIsClient && bIsClient) return 1
      if (aIsOpponent && !bIsOpponent && !bIsClient) return -1
      if (!aIsOpponent && bIsOpponent && !aIsClient) return 1
      return 0
    })
  })

  // Determine if we should show a separator after priority entities
  const shouldShowSeparator = computed(() => {
    // Only show separator if we have more than just client and opponent
    if (sortedEntities.value.length <= 2) return false

    // Count priority entities (client and opponent)
    const priorityCount = sortedEntities.value.filter((e) => isPriorityEntity(e)).length

    // If we have priority entities and we have more than just those, show separator
    return priorityCount > 0 && priorityCount < sortedEntities.value.length
  })

  const selectEntityRelationship = (entity: EntityWithRelationship) => {
    selectedEntityRelationship.value = entity
  }

  const addNewEntity = async () => {
    // Close any open popovers
    openPopoverId.value = null

    const newEntityRelationship = await caseStore.addEntity({
      entity_type: EntityTypeEnum.NATURAL_PERSON
    } as CustomerEntity)
    selectedEntityRelationship.value = newEntityRelationship
  }

  // Function to show the delete confirmation modal
  const confirmDeleteEntity = (entity: EntityWithRelationship) => {
    entityToDelete.value = entity
    showDeleteConfirmation.value = true

    // Close any open popover
    openPopoverId.value = null
  }

  const togglePopover = (uuid: string) => {
    openPopoverId.value = openPopoverId.value === uuid ? null : uuid
  }

  // Function to actually delete the entity after confirmation
  const deleteEntity = async () => {
    try {
      if (!entityToDelete.value || !caseRecord.value?.uuid) return

      await caseStore.deleteEntity(entityToDelete.value)

      // If the deleted entity was selected, clear the selection
      if (selectedEntityRelationship.value?.uuid === entityToDelete.value.uuid) {
        selectedEntityRelationship.value = null
      }

      // Reset the confirmation modal state
      showDeleteConfirmation.value = false
      entityToDelete.value = null
    } catch (error) {
      console.error('Error deleting entity:', error)
    }
  }

  // Auto-select entity based on URL parameter or default to client
  watch(
    [caseRecord, () => route.params.entityUuid],
    ([newCase, entityUuid]) => {
      if (newCase?.data.entities) {
        if (entityUuid) {
          // If entity UUID is provided in URL, select that entity
          const entityFromUrl = newCase.data.entities.find(
            (entity) => entity.entity.uuid === entityUuid
          )
          if (entityFromUrl) {
            selectEntityRelationship(entityFromUrl)
            return
          }
        }

        // If no entity UUID in URL or entity not found, default to client
        const clientEntity = newCase.data.entities.find((entity) =>
          entity.relationships.some(
            (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
          )
        )
        if (clientEntity) {
          selectEntityRelationship(clientEntity)
        }
      }
    },
    { immediate: true }
  )

  const selectedEntitiesWithRelationships = ref<Array<string>>([])
  const hasInitializedSelection = ref(false)

  // Reset auto-selection state when switching cases
  watch(
    () => caseRecord.value?.uuid,
    (newUuid, oldUuid) => {
      if (newUuid && newUuid !== oldUuid) {
        hasInitializedSelection.value = false
      }
    }
  )

  // Initialize with all enabled entities selected by default
  watch(
    [clientEntityRelationship, sortedEntities, () => props.unifiedExportMode],
    ([newClientUuid, entities, unifiedMode]) => {
      // Only auto-select all entities on initial load in unified export mode
      if (entities.length > 0 && unifiedMode && !hasInitializedSelection.value) {
        const allEnabledEntities = entities
          .filter((entity) => isEntityEnabled(entity))
          .map((entity) => entity.uuid)

        selectedEntitiesWithRelationships.value = [...new Set(allEnabledEntities)]
        emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
        hasInitializedSelection.value = true
      } else if (
        newClientUuid &&
        !selectedEntitiesWithRelationships.value.includes(newClientUuid)
      ) {
        selectedEntitiesWithRelationships.value.push(newClientUuid)
        emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
        if (!hasInitializedSelection.value && unifiedMode) {
          hasInitializedSelection.value = true
        }
      }
    },
    { immediate: true }
  )

  const updateSelectedEntitiesWithRelationships = (entityUuid: string | undefined) => {
    if (!entityUuid) return

    const index = selectedEntitiesWithRelationships.value.indexOf(entityUuid)
    if (index !== -1) {
      // Don't allow deselection of client entity
      if (entityUuid === clientEntityRelationship.value) return

      selectedEntitiesWithRelationships.value.splice(index, 1)
    } else {
      selectedEntitiesWithRelationships.value.push(entityUuid)
    }

    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  // Deselect all entities except the client entity
  const deselectAll = () => {
    if (clientEntityRelationship.value) {
      selectedEntitiesWithRelationships.value = [clientEntityRelationship.value]
    } else {
      selectedEntitiesWithRelationships.value = []
    }
    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  const selectAll = () => {
    const allEnabledEntities = sortedEntities.value
      .filter((entity) => isEntityEnabled(entity))
      .map((entity) => entity.uuid)

    // Set the selected entities to all enabled entities
    selectedEntitiesWithRelationships.value = [...new Set(allEnabledEntities)]
    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  // Toggle between select all and deselect all
  const toggleSelectAll = () => {
    // Always select all if clicked, regardless of current state
    // This is different from the previous behavior where it toggled between select all and deselect all
    selectAll()
  }

  // Check if an entity is currently selected
  const isEntitySelected = (entityUuid: string) => {
    return selectedEntitiesWithRelationships.value.includes(entityUuid)
  }

  // Check if all enabled entities are selected
  const areAllEntitiesSelected = computed(() => {
    const enabledEntities = sortedEntities.value
      .filter((entity) => isEntityEnabled(entity))
      .map((entity) => entity.uuid)

    return enabledEntities.every((uuid) => selectedEntitiesWithRelationships.value.includes(uuid))
  })

  // Check if any entities are selected (including client)
  const hasSelectedEntities = computed(() => {
    return selectedEntitiesWithRelationships.value.length > 0
  })

  // Check if only the client entity is selected
  const onlyClientSelected = computed(() => {
    return (
      selectedEntitiesWithRelationships.value.length === 1 &&
      selectedEntitiesWithRelationships.value[0] === clientEntityRelationship.value
    )
  })

  // Check if any non-client entities are selected
  const hasNonClientSelected = computed(() => {
    return hasSelectedEntities.value && !onlyClientSelected.value
  })

  const divClass = computed(() => {
    return props.unifiedExportMode
      ? 'rounded-xl border bg-white p-6 shadow-md'
      : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm'
  })

  const errorStatesByEntityUuid = reactive<{ [key: string]: number }>({})
  const handleTotalNumberOfErrors = (entityUuid: string, totalNumberOfErrors: number) => {
    errorStatesByEntityUuid[entityUuid] = totalNumberOfErrors
    // Emit the total number of errors only for selected entities, we need to get the selected entities within the relationship
    const selectedUuids = sortedEntities.value
      .filter((entity) => isEntitySelected(entity.uuid ? entity.uuid : ''))
      .map((entityRelationship) => entityRelationship.entity.uuid)
    const total = selectedUuids.reduce((sum, uuid) => {
      return sum + (errorStatesByEntityUuid[uuid] || 0)
    }, 0)
    emit('onTotalNumberOfErrors', total)
  }

  // Determine if errors should be shown for a given entity
  const shouldShowErrorsForEntity = (entity) => {
    // Only show errors if the entity has errors AND
    // (is selected for export OR is currently being edited)

    const shouldShow =
      errorStatesByEntityUuid[entity.entity.uuid] &&
      (isEntitySelected(entity.uuid) || selectedEntityRelationship.value?.uuid === entity.uuid)

    return shouldShow
  }
</script>
<template>
  <div :id="$attrs.id as string">
    <div :class="divClass">
      <div class="flex gap-5 pb-4">
        <div class="w-1/4 pr-4">
          <div class="flex items-center gap-2">
            <span class="text-lg font-semibold text-gray-800">{{ t('parties') }}</span>
            <HelpLink
              :url="KNOWLEDGE_BASE_URLS.entities"
              :tooltip="t('helpEntities')"
              location="entities"
            />
            <span
              v-if="unifiedExportMode"
              class="ml-2 text-sm font-normal text-gray-500"
            >
              ({{ selectedEntitiesWithRelationships.length }} {{ t('selected') }})
            </span>
          </div>
        </div>
        <div class="w-3/4">
          <div class="flex items-center justify-end gap-2">
            <!-- No bulk select button here anymore, moved to entity list -->
            <button
              class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                disabled:pointer-events-none disabled:opacity-50"
              @click="addNewEntity"
            >
              <PlusIcon class="h-5 w-5" />
              {{ t('addNewParty') }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex gap-5">
        <div class="w-1/4 border-r-2 pr-4">
          <div class="">
            <!-- Bulk Select/Deselect Toggle Button for unified export mode -->
            <div
              v-if="unifiedExportMode && sortedEntities.length > 0"
              class="mb-2 flex items-center justify-between rounded-lg p-2"
            >
              <Button
                variant="outline"
                size="sm"
                class="h-6 w-6 p-0"
                :class="{
                  'border border-purple-500 bg-purple-500': hasNonClientSelected,
                  'border border-purple-500 bg-white': !hasNonClientSelected
                }"
                @click="
                  hasNonClientSelected && areAllEntitiesSelected ? deselectAll() : toggleSelectAll()
                "
              >
                <Minus
                  v-if="hasNonClientSelected"
                  class="h-4 w-4 text-white"
                />
              </Button>
              <div class="ml-2 flex min-w-0 flex-grow text-sm text-gray-600">
                {{ t('selectAll') }}
              </div>
            </div>
            <!-- Priority entities (client and opponent) -->
            <template
              v-for="entity in sortedEntities"
              :key="entity.uuid"
            >
              <!-- Only render priority entities in this first loop -->
              <div
                v-if="isPriorityEntity(entity)"
                class="flex items-center justify-between rounded-lg p-2 hover:bg-gray-50"
                :class="{
                  'bg-gray-100': selectedEntityRelationship?.uuid === entity.uuid,
                  'pointer-events-none': !isEntityEnabled(entity)
                }"
                @click="selectEntityRelationship(entity)"
              >
                <CheckboxField
                  v-if="unifiedExportMode"
                  :checked-value="true"
                  :unchecked-value="false"
                  :model-value="isEntitySelected(entity.uuid)"
                  :name="`entity-selection-${entity.uuid}`"
                  :disabled="!isEntityEnabled(entity) || isClientEntity(entity)"
                  @update:model-value="
                    (val) => updateSelectedEntitiesWithRelationships(entity.uuid)
                  "
                />
                <div
                  class="ml-0.5 flex min-w-0 flex-grow cursor-pointer items-center"
                  :class="{
                    'text-gray-600': isEntityEnabled(entity),
                    'text-gray-500': !isEntityEnabled(entity)
                  }"
                  @click="selectEntityRelationship(entity)"
                >
                  <div class="mr-3 flex flex-shrink-0 items-center justify-center">
                    <User
                      v-if="entity.entity?.entity_type === EntityTypeEnum.NATURAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Building2
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Landmark
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.INSTITUTION"
                      class="h-6 w-6 text-gray-600"
                    />
                    <User
                      v-else
                      class="h-6 w-6 text-gray-600"
                    />
                    <!-- Default to User icon if type not specified -->
                  </div>

                  <div class="min-w-0 flex-grow">
                    <div
                      class="truncate text-sm font-semibold text-gray-600"
                      :class="{
                        'text-gray-600': isEntityEnabled(entity),
                        'text-gray-500': !isEntityEnabled(entity)
                      }"
                    >
                      {{ formatEntityName(entity.entity) }}
                    </div>
                    <div
                      class="text-xs text-gray-500"
                      :class="{
                        'text-gray-600': isEntityEnabled(entity),
                        'text-gray-500': !isEntityEnabled(entity)
                      }"
                    >
                      <span
                        v-for="(relationship, index) in entity.relationships"
                        :key="relationship.uuid"
                        class="text-gray-600"
                      >
                        {{ t(relationship.relationship_type)
                        }}{{ index < entity.relationships.length - 1 ? ', ' : '' }}
                      </span>
                    </div>
                    <div
                      v-if="!isEntityEnabled(entity)"
                      class="text-xs text-gray-500"
                    >
                      {{ t('notEnabledInExport') }}
                    </div>
                  </div>
                  <div
                    v-if="shouldShowErrorsForEntity(entity)"
                    class="inline-flex items-center gap-x-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700"
                  >
                    {{ errorStatesByEntityUuid[entity.entity.uuid] }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Add a horizontal rule separator after priority entities -->
            <hr
              v-if="shouldShowSeparator"
              class="my-3 border-gray-200"
            />

            <!-- Non-priority entities (all other types) -->
            <template
              v-for="entity in sortedEntities"
              :key="`non-priority-${entity.uuid}`"
            >
              <!-- Only render non-priority entities in this second loop -->
              <div
                v-if="!isPriorityEntity(entity)"
                class="mb-2 flex min-h-[52px] items-center justify-between rounded-lg p-2 hover:bg-gray-50"
                :class="{
                  'bg-gray-100': selectedEntityRelationship?.uuid === entity.uuid
                }"
              >
                <CheckboxField
                  v-if="unifiedExportMode"
                  :checked-value="true"
                  :unchecked-value="false"
                  :model-value="isEntitySelected(entity.uuid)"
                  :name="`entity-selection-${entity.uuid}`"
                  :disabled="!isEntityEnabled(entity)"
                  @update:model-value="
                    (val) => updateSelectedEntitiesWithRelationships(entity.uuid)
                  "
                />
                <div
                  class="ml-0.5 flex min-w-0 flex-grow cursor-pointer items-center"
                  :class="{
                    'pointer-events-none': !isEntityEnabled(entity)
                  }"
                  @click="selectEntityRelationship(entity)"
                >
                  <div class="mr-3 flex flex-shrink-0 items-center justify-center">
                    <User
                      v-if="entity.entity?.entity_type === EntityTypeEnum.NATURAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Building2
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Landmark
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.INSTITUTION"
                      class="h-6 w-6 text-gray-600"
                    />
                    <User
                      v-else
                      class="h-6 w-6 text-gray-600"
                    />
                    <!-- Default to User icon if type not specified -->
                  </div>

                  <div class="min-w-0 flex-grow">
                    <div class="truncate text-sm font-semibold text-gray-600">
                      {{ formatEntityName(entity.entity) }}
                    </div>
                    <div class="text-xs text-gray-500">
                      <span
                        v-for="(relationship, index) in entity.relationships"
                        :key="relationship.uuid"
                        class="text-gray-600"
                      >
                        {{ t(relationship.relationship_type)
                        }}{{ index < entity.relationships.length - 1 ? ', ' : '' }}
                      </span>
                    </div>
                    <div
                      v-if="!isEntityEnabled(entity)"
                      class="text-xs text-gray-500"
                    >
                      {{ t('notEnabledInExport') }}
                    </div>
                  </div>
                  <div
                    v-if="shouldShowErrorsForEntity(entity)"
                    class="inline-flex items-center gap-x-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700"
                  >
                    {{ errorStatesByEntityUuid[entity.entity.uuid] }}
                  </div>
                </div>

                <Popover
                  :open="openPopoverId === entity.uuid"
                  @update:open="
                    (val) => (val ? togglePopover(entity.uuid || '') : (openPopoverId = null))
                  "
                >
                  <PopoverTrigger
                    class="ml-2 flex-shrink-0 rounded-full p-1 hover:bg-gray-200 focus:outline-none"
                  >
                    <MoreVertical class="h-4 w-4 text-gray-500" />
                  </PopoverTrigger>
                  <PopoverContent class="w-48 p-1">
                    <div class="flex flex-col">
                      <button
                        class="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        @click.stop="confirmDeleteEntity(entity)"
                      >
                        <Trash2 class="mr-2 h-4 w-4" />
                        {{ t('delete_party') }}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </template>
          </div>
        </div>
        <div class="flex-1">
          <Entity
            v-if="selectedEntityRelationship"
            :entity-relationship-uuid="selectedEntityRelationship.uuid!"
            :insurance-info-enabled="insuranceInfoEnabled"
            :custom-export-validation="customExportValidation ?? null"
            @total-number-of-errors="handleTotalNumberOfErrors"
          />
          <div
            v-for="entityRelationship in selectedEntitiesWithRelationships"
            v-show="false"
            :key="entityRelationship"
          >
            <Entity
              :entity-relationship-uuid="entityRelationship"
              :insurance-info-enabled="insuranceInfoEnabled"
              :custom-export-validation="customExportValidation"
              @total-number-of-errors="handleTotalNumberOfErrors"
            />
          </div>
          <div
            v-if="!selectedEntityRelationship"
            class="flex h-full items-center justify-center text-gray-500"
          >
            {{ t('selectParty') }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <AlertDialog
    :open="showDeleteConfirmation"
    @update:open="showDeleteConfirmation = $event"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('confirm_delete') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('confirm_delete_party_message') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeleteConfirmation = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700"
          @click="deleteEntity()"
        >
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
